
import { defineComponent , PropType, ref } from 'vue'
import useNotyf from '../../../composable/useNotyf'
import ProductLineRow from '../../form/productindex/ProductLineRow.vue'
import LegVolumesCalculatorModal from '../../form/productindex/LegVolumesCalculatorModal.vue'
import VCard from '../../../components/base/card/VCard.vue'
import VField from '../../../components/base/form/VField.vue'
import VControl from '../../../components/base/form/VControl.vue'
import VCheckbox from '../../../components/base/form/VCheckbox.vue'
import VSwitchBlock from '../../../components/base/form/VSwitchBlock.vue'
import VButton from '../../../components/base/button/VButton.vue'
import { mapState , mapActions , mapGetters } from 'vuex'
import store , { ActionTypes } from '../../../store'
import { ProductLine , Index, User } from '../../../types'
import { AxiosError } from "axios"
import ApexChart from 'vue3-apexcharts'
import { useRoute } from "vue-router";

interface RowPayload {
  row : number;
  values : Record<string , any>[];
}
interface CopyInData {
  is_key_contract: boolean;
  product: number;
  price_ratio: number;
  trade_ratio: number;
  transaction_type: string;
}
interface Data {
  form : Record<string , any>;
  minMax : number[];
  apiErrors? : Record<string , any>[];
  copiedOut : boolean;
  copiedCIX : boolean;
  copyInTextExec: string;
  copyInTextPrc: string;
  copyInList: CopyInData [];
  showStratToggleBtnExec: boolean;
  showStratToggleBtnPrc: boolean;
  showCopyInValues:boolean;
  showLegVolumesModal: boolean;
  initialKeyVolume: number | null;
  riskData: any[];
  loadingRiskData: boolean;
}
const notyf = useNotyf()

export default defineComponent( {
  name : 'StrategyForm' ,
  components : {
    ProductLineRow ,
    LegVolumesCalculatorModal ,
    ApexChart ,
    VCard ,
    VField ,
    VControl ,
    VButton ,
    VCheckbox ,
    VSwitchBlock
  } ,
  props : {
    index : {
      type : Object as PropType<Index> ,
      required : false
    },
    tabvalue: {
      type : String ,
      required : false ,
      default : 'ASSIGNED'
    } ,
    users: {
      type: Array as PropType<User[]>
    },
  } ,
  emit:['rowUpdate','rowRemove'],
  data(): Data {
    return {
      form : {
        strat_name : '' ,
        is_submitted : false ,
        creator : 0 ,
        trader : 0,
        is_yield : false ,
        is_forward : false ,
        productline_index : [] ,
      } ,
      minMax : [ 2 , 30 ] ,
      apiErrors : undefined ,
      copiedOut : false ,

      copiedCIX : false ,
      copyInTextExec: ``,
      copyInTextPrc: ``,
      copyInList: [],
      showStratToggleBtnExec: true,
      showStratToggleBtnPrc: true,
      showCopyInValues:false,
      showLegVolumesModal: false,
      initialKeyVolume: null,
      riskData: [],
      loadingRiskData: false,
    }
  } ,
  setup() {
    const route = useRoute()
    return {route}
  } ,
  computed : {
    ... mapState( [ 'products' , 'user' ] ) ,
    ... mapGetters( [ 'isStaff' ] ) ,
    productLineErrors() : Record<string , any>[] {
      if ( this.apiErrors !== undefined ) {
        if ( 'productline_index' in this.apiErrors ) {
          return this.apiErrors[ 'productline_index' ]
        }
      }
      return []
    } ,
    isRemovable() : boolean {
      return this.form.productline_index.length > this.minMax[ 0 ]
    } ,
    isEditForm() : boolean {
      return this.$route.path.includes( 'edit' )
    } ,

  } ,
  methods : {
    ... mapActions( {
      createIndex : ActionTypes.CREATE_INDEX ,
      updateIndex : ActionTypes.UPDATE_INDEX ,
      submitCalcWeights : ActionTypes.SUBMIT_CALC_WEIGHTS ,
      submitHouseWeights : ActionTypes.SUBMIT_HOUSE_WEIGHTS ,
      submitGenerateName : ActionTypes.SUBMIT_GENERATE_NAME ,
      fetchRiskList : ActionTypes.FETCH_RISK_LIST
    } ) ,

    fieldUpdate( event : Event ) : void {
      const target = event.target as HTMLInputElement
      this.form[ target.name ] = target.value
    } ,
    initRows( numRows : number ) : void {
      this.form[ 'productline_index' ] = [ ... Array( numRows ).keys() ].map( () => this.emptyRow() )
    } ,
    rowAdd() : void {
      const [ , max ] = this.minMax
      if ( this.form[ 'productline_index' ].length < max ) {
        this.form[ 'productline_index' ].push( this.emptyRow() )
      } else {
        return
      }
    } ,
    rowUpdate( rowPayload : RowPayload ) : void {
      const row = this.form.productline_index[ rowPayload.row ]
      let updateRadios = false
      rowPayload.values.forEach( ( item ) => {
        row[ item.name ] = item.value
        if ( item.name === 'is_key_contract' ) {
          updateRadios = true
        }
      } );
      if ( updateRadios ) {
        this.form.productline_index.filter( ( row : ProductLine , index : number ) => index !== rowPayload.row ).forEach( ( row : ProductLine ) => row[ 'is_key_contract' ] = false )
      }
    } ,
    rowRemove( row : number ) : void {
      this.form.productline_index = this.form.productline_index.filter( ( item : ProductLine , index : number ) => row !== index )
    } ,
    calcHouseWeights() : void {
      this.submitCalc( this.submitHouseWeights );
    } ,
    calcWeights() : void {
      this.submitCalc( this.submitCalcWeights )
    } ,
    generateName() : void {
      this.submitCalc( this.submitGenerateName )
    } ,
    submitCalc( action : Function ) : void {
      action( this.form ).then( ( response : Index ) => {
        this.form = { ... this.form , ... response }
      } ).catch( ( error : AxiosError ) => {
      } )
    } ,
    buildIndex() : void {
      const payload = { ... this.form } as Index
      if ( this.isEditForm && this.index ) {
        payload.id = this.index.id
        this.updateIndex( payload ).then( ( response ) => {
          notyf.success(`Strategy updated!`);
          notyf.warning(`Your page will auto-refresh in 1 second to update strategies`);
          this.form.is_submitted=true
          setTimeout(function () { location.reload(); }, 1000);
          this.$router.push( { name : 'StrategiesUpdate' , params : { id : response.id } } )
        } ).catch( ( error : AxiosError ) => {
          this.apiErrors = error.response?.data
        } )
      } else {
        this.createIndex( payload ).then( ( response ) => {
          notyf.success(`Strategy created!`);
          notyf.warning(`Your page will auto-refresh in 1 second to update strategies`);
          setTimeout(function () { location.reload(); }, 1000);

          if ( this.isStaff ) {
            this.$router.push( { name : 'StaffStrategiesUpdate' , params : { id : response.id } } )
          } else {
            this.$router.push( { name : 'StrategiesUpdate' , params : { id : response.id } } )
          }
        } ).catch( ( error : AxiosError ) => {
          this.apiErrors = error.response?.data
        } )
      }
    } ,
    shareIndex() : void {
      const payload = { ... this.form } as Index
      payload[ 'is_submitted' ] = true
      if ( this.isEditForm && this.index ) {
        payload.id = this.index.id
        this.updateIndex( payload ).then( ( response ) => {
          if ( this.isStaff ) {
            this.$router.push( { name : 'StaffStrategiesUpdate' , params : { id : response.id } } )
          } else {
            this.$router.push( { name : 'StrategiesUpdate' , params : { id : response.id } } )
          }
        } ).catch( ( error : AxiosError ) => {
          this.apiErrors = error.response?.data
        } )
      } else {
        this.createIndex( payload ).then( ( response ) => {
          if ( this.isStaff ) {
            this.$router.push( { name : 'StaffStrategiesUpdate' , params : { id : response.id } } )
          } else {
            this.$router.push( { name : 'StrategiesUpdate' , params : { id : response.id } } )
          }
        } ).catch( ( error : AxiosError ) => {
          this.apiErrors = error.response?.data
        } )
      }
    } ,
    async loadRiskData() : Promise<void> {
      this.loadingRiskData = true
      try {
        const response = await this.fetchRiskList()
        this.riskData = response.results || []
        if (this.riskData.length > 0) {
          const errors = this.riskData.filter((r: any) => r.error !== null)
          if (errors.length > 0) {
            notyf.warning(`${errors.length} product(s) failed to calculate risk values`)
          }
        }
      } catch (error: any) {
        notyf.error('Failed to fetch risk data: ' + (error.message || 'Unknown error'))
        this.riskData = []
      } finally {
        this.loadingRiskData = false
      }
    } ,
    emptyRow() : Record<string , any> {
      return {
        is_key_contract : false ,
        product : 0 ,
        price_ratio : '0' ,
        trade_ratio : '0' ,
        transaction_type : 'BUY'
      }
    } ,
    copyCIXSuccess() {
      this.copiedCIX = true
      const timeoutID = window.setTimeout( () => {
        this.copiedCIX = false
        window.clearTimeout( timeoutID );
      } , 1000 )
    } ,

    copyOutProductLines() {
      let outString = ''
      this.form.productline_index.forEach( ( productline : ProductLine ) => {
        // Use transaction_type_new_ion which returns '1' for BUY and '2' for SELL
        const trTypeValue = productline.transaction_type_new_ion || (productline.transaction_type === 'BUY' ? '1' : '2')
        outString += `${ trTypeValue }\t${ Math.abs( productline.price_ratio ) }\t${ Math.abs( productline.trade_ratio ) }\n`
      } )
      return outString
    } ,
    copyOutSuccess() {
      this.copiedOut = true
      const timeoutID = window.setTimeout( () => {
        this.copiedOut = false
        window.clearTimeout( timeoutID );
      } , 1000 )
    },
    copyInProductLinesExec() {
      this.copyInList.length = 0;
      this.showCopyInValues=true;
      const prodsList = this.copyInTextExec.split(/\r?\n/g);
      prodsList.map((prod,index) => {
        let prodItem = prod.trim().split(/\r?\t/g);
        let productId = 0 ;

        this.products.map( (item:any) =>{
          if(prodItem[0]===item.name){
            productId=item.id;
          }
        });

        this.copyInList.push({
          is_key_contract: index===0,
          product: productId,
          price_ratio: 0,
          trade_ratio: Number(prodItem[1]),
          transaction_type: Number(prodItem[1])>0 ? 'BUY' : 'SELL',
        });
      });

      this.form.productline_index=JSON.parse( JSON.stringify(this.copyInList));

      this.showStratToggleBtnExec = true;
    },
    copyInProductLinesPrc() {
      this.copyInList.length = 0;
      this.showCopyInValues=true;
      const prodsList = this.copyInTextPrc.split(/\r?\n/g);
      prodsList.map((prod,index) => {
        let prodItem = prod.trim().split(/\r?\t/g);
        let productId = 0 ;

        this.products.map( (item:any) =>{
          if(prodItem[0]===item.name){
            productId=item.id;
          }
        });

        this.copyInList.push({
          is_key_contract: index===0,
          product: productId,
          price_ratio: Number(prodItem[1]),
          trade_ratio: 0,
          transaction_type: Number(prodItem[1])>0 ? 'BUY' : 'SELL',
        });
      });

      this.form.productline_index=JSON.parse( JSON.stringify(this.copyInList));

      this.showStratToggleBtnPrc = true;
    },
    handleLegVolumesApply(data: any) {
      // Log the calculated volumes
      console.log('Contract volumes calculated:', data);
      
      // Show success notification
      notyf.success(`Contract volumes calculated successfully! Key volume: ${data.keyVolume}`);
      
      // Dispatch custom event to save to history
      const strategyName = this.form.strat_name || this.index?.strat_name || 'Unnamed Strategy';
      const keyProductName = data.keyProductName || 'Unknown';
      
      window.dispatchEvent(new CustomEvent('leg-volume-calculated', {
        detail: {
          strategyId: this.index?.id || 0,
          strategyName: strategyName,
          keyVolume: data.keyVolume,
          keyProductName: keyProductName,
          productCount: data.volumes.length,
          productLines: this.form.productline_index,
          products: this.products
        }
      }));
    },
    sortedUsers(users:Array<User[]>) : any {
      if(users===undefined){
       store.dispatch( ActionTypes.FETCH_USERS ).then( response => { return response.sort( ( a : any , b : any ) => a.first_name.localeCompare( b.first_name ) )})

      } else{
        return users.sort( ( a : any , b : any ) => a.first_name.localeCompare( b.first_name ) );
      }

    }
  } ,
  created() {
    if ( this.isStaff ) {
      store.dispatch( ActionTypes.FETCH_USERS )
    }


    const [ min , ] = this.minMax
    if ( this.index ) {
      this.form = JSON.parse( JSON.stringify( this.index ) )
    } else {
      this.initRows( min )
    }
  },
  mounted() {
    // Check for query parameters to open calculator
    const route = this.$route;
    if (route.query.openCalculator === 'true') {
      this.showLegVolumesModal = true;
      
      // If there's a keyVolume in query, we'll need to pass it to the modal
      // This would require updating the modal to accept an initial value prop
      const keyVolume = route.query.keyVolume;
      if (keyVolume) {
        // Store it for when the modal opens
        this.initialKeyVolume = Number(keyVolume);
      }
    }
  }
} )
